<template>
  <div ref="posterRoot" class="team-share-poster" :class="{ 'export-mode': exportMode }">
    <div class="poster-top">
      <template v-if="isNotStarted">
        <div class="pre-match-line">
          <span class="team-short team-a">{{ displayTeamA }}</span>
          <span class="team-count">{{ teamACount }}</span>
          <span class="divider">:</span>
          <span class="team-count">{{ teamBCount }}</span>
          <span class="team-short team-b">{{ displayTeamB }}</span>
        </div>
      </template>
      <template v-else>
        <div class="live-score">{{ totalPoints }} <span>Pts</span> </div>
      </template>
    </div>

    <div class="players-container">
      <div class="pitch-bg">
        <div class="position-section wk-section">
          <div class="section-label">
            <span class="section-label-text">WICKET-KEEPERS</span>
          </div>
          <div class="players-row">
            <PreviewPlayerCard v-for="player in groupedPlayers.WK" :key="player.pid || player.id" :player="player"
              :match-info="matchInfo">
              <template #avatar="{ player }">
                <ShareAvatarBadge :short-name="player?.name" :size="55" />
              </template>
            </PreviewPlayerCard>
          </div>
        </div>

        <div class="position-section bat-section">
          <div class="section-label">
            <span class="section-label-text">BATTERS</span>
          </div>
          <div class="players-row">
            <PreviewPlayerCard v-for="player in groupedPlayers.BAT" :key="player.pid || player.id" :player="player"
              :match-info="matchInfo">
              <template #avatar="{ player }">
                <ShareAvatarBadge :short-name="player?.name" :size="55" />
              </template>
            </PreviewPlayerCard>
          </div>
        </div>

        <div class="position-section ar-section">
          <div class="section-label">
            <span class="section-label-text">ALL-ROUNDERS</span>
          </div>
          <div class="players-row">
            <PreviewPlayerCard v-for="player in groupedPlayers.AR" :key="player.pid || player.id" :player="player"
              :match-info="matchInfo">
              <template #avatar="{ player }">
                <ShareAvatarBadge :short-name="player?.name" :size="55" />
              </template>
            </PreviewPlayerCard>
          </div>
        </div>

        <div class="position-section bowl-section">
          <div class="section-label">
            <span class="section-label-text">BOWLERS</span>
          </div>
          <div class="players-row">
            <PreviewPlayerCard v-for="player in groupedPlayers.BOWL" :key="player.pid || player.id" :player="player"
              :match-info="matchInfo">
              <template #avatar="{ player }">
                <ShareAvatarBadge :short-name="player?.name" :size="55" />
              </template>
            </PreviewPlayerCard>
          </div>
        </div>
      </div>
    </div>

    <SharePosterInviteFooter :user-name="displayUserName" :team-name="displayTeamName"
      :match-text="`${displayTeamA} vs ${displayTeamB}`" :invite-code="inviteInfo.invCode || ''"
      :qr-data-url="qrDataUrl" />
  </div>
</template>

<script setup>
import ShareAvatarBadge from '@/components/share/ShareAvatarBadge.vue'
import SharePosterInviteFooter from '@/components/share/SharePosterInviteFooter.vue'
import PreviewPlayerCard from '@/components/teamPreview/PreviewPlayerCard.vue'
import { MatchStatus } from '@/views/contests/matchesType.js'

const props = defineProps({
  exportMode: {
    type: Boolean,
    default: false,
  },
  players: {
    type: Array,
    default: () => [],
  },
  matchInfo: {
    type: Object,
    default: () => ({}),
  },
  teamMeta: {
    type: Object,
    default: () => ({}),
  },
  userInfo: {
    type: Object,
    default: () => ({}),
  },
  inviteInfo: {
    type: Object,
    default: () => ({}),
  },
  qrDataUrl: {
    type: String,
    default: '',
  },
})

const posterRoot = ref(null)

const groupedPlayers = computed(() => {
  const groups = {
    WK: [],
    BAT: [],
    AR: [],
    BOWL: [],
  }
  props.players.forEach((player) => {
    if (groups[player.position]) {
      groups[player.position].push(player)
    }
  })
  return groups
})

const displayUserName = computed(() => {
  const text = String(props.userInfo?.nickName || 'User').trim()
  return text.length > 14 ? `${text.slice(0, 14)}...` : text
})
const displayTeamName = computed(() => String(props.teamMeta?.teamName || '').trim())
const displayTeamA = computed(() => props.matchInfo?.teamAShort || '--')
const displayTeamB = computed(() => props.matchInfo?.teamBShort || '--')
const isNotStarted = computed(() => {
  const status = Number(props.matchInfo?.matchStatus || props.matchInfo?.status || MatchStatus.NOT_STARTED)
  return status === MatchStatus.NOT_STARTED
})
const teamACount = computed(() => {
  const teamaId = props.matchInfo?.teamaId
  if (teamaId) {
    return props.players.filter(player => String(player.teamId) === String(teamaId)).length
  }
  return props.players.filter((player) => {
    const short = String(player.team || player.teamShortName || '').trim().toUpperCase()
    return short && short === String(displayTeamA.value || '').trim().toUpperCase()
  }).length
})
const teamBCount = computed(() => {
  const teambId = props.matchInfo?.teambId
  if (teambId) {
    return props.players.filter(player => String(player.teamId) === String(teambId)).length
  }
  return props.players.filter((player) => {
    const short = String(player.team || player.teamShortName || '').trim().toUpperCase()
    return short && short === String(displayTeamB.value || '').trim().toUpperCase()
  }).length
})
const totalPoints = computed(() => {
  return props.players.reduce((sum, player) => sum + Number(player.points || player.point || 0), 0)
})

const getPosterElement = () => posterRoot.value

defineExpose({
  getPosterElement,
})
</script>

<style scoped lang="less">
.team-share-poster {
  width: min(360px, 100%);
  border-radius: 16px;
  overflow: hidden;

  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  .poster-top {
    padding: 8px 16px;
    width: 100%;
    background: #18212e;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;

    .pre-match-line {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-weight: 700;

      .team-short {
        font-size: 12px;
        line-height: 16px;
        padding: 1px 5px;
        border-radius: 3px;
        background: rgba(255, 255, 255, 0.18);
        color: #ffffff;
      }

      .team-count {
        font-size: 20px;
        line-height: 28px;
        color: #ffffff;
        font-family: Heebo-Bold, sans-serif;
      }

      .divider {
        font-size: 30px;
        line-height: 24px;
        color: #ffffff;
      }
    }

    .live-score {
      font-size: 20px;
      line-height: 30px;
      font-weight: 800;
      color: #ffffff;
      font-family: Heebo-Bold, sans-serif;

      span {
        font-size: 12px;
        line-height: 16px;
        color: #ffffff;
        font-family: Heebo-Regular, sans-serif;
      }
    }
  }

  .players-container {
    max-height: 200px;
    overflow-y: auto;
    padding: 16px 12px;
    position: relative;
    background: url('/src/assets/images/team-preview-bg-D-9qbkqb.webp') no-repeat center;
    background-size: 100% auto;
    -webkit-overflow-scrolling: touch;

    // &::before {
    //   content: '';
    //   position: absolute;
    //   top: 0;
    //   left: 0;
    //   right: 0;
    //   bottom: 0;
    //   background: url('/src/assets/images/team-preview-bg-DLs6Yne5.webp') no-repeat center;
    //   background-size: 100% auto;
    // }

    .pitch-bg {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-height: 100%;
    }

    .position-section .section-label {
      text-align: center;
      margin-bottom: 10px;
    }

    .position-section .section-label-text {
      font-size: 9px;
      color: rgba(255, 255, 255, 0.8);
      margin: 5px auto;
      letter-spacing: 1px;
      display: inline-block;
      text-transform: uppercase;
      border-radius: 10px;
      background: linear-gradient(90deg, rgba(24, 29, 41, 0), rgba(24, 29, 41, 0.26) 22.8%, rgba(24, 29, 41, 0.26) 49.52%, rgba(24, 29, 41, 0.26) 76.3%, rgba(24, 29, 41, 0) 98.08%);
      font-family: Heebo-SemiBold, sans-serif;
      padding: 2px 10px;
    }

    .players-row {
      display: flex;
      justify-content: center;
      gap: 16px;
      flex-wrap: wrap;
    }
  }

  &.export-mode {
    height: auto;

    .players-container {
      max-height: none;
      height: auto;
      overflow: visible;
    }
  }
}
</style>
